import React, {useState, useEffect, useRef} from 'react';
import {useHistory} from "react-router-dom";
import {withTranslation} from 'react-i18next';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import axios from "axios";
import {Panel} from "primereact/panel";
import {InputText} from "primereact/inputtext";

const ContactListComponent = (props) => {

  const {t, detailedSearchVisible, view, onDetailedSearchActiveChange} = props;
  const history = useHistory();

  const LIST_STORAGE_KEY = 'lastContactListState';
  const DS_STORAGE_KEY = 'lastContactDetailedSearchForm';
  const DEFAULT_SORT_FIELD = 'name';
  const DEFAULT_SORT_ORDER = 1;
  const savedListState = localStorage.getItem(LIST_STORAGE_KEY)
    ? JSON.parse(localStorage.getItem(LIST_STORAGE_KEY))
    : null;
  const savedDetailedForm = localStorage.getItem(DS_STORAGE_KEY)
    ? JSON.parse(localStorage.getItem(DS_STORAGE_KEY))
    : null;

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rows, setRows] = useState(
    savedListState?.rows || Number(process.env.REACT_APP_LIST_ROW_DEFAULT)
  );
  const dt = useRef(null);
  const [detailedSearchQuery, setDetailedSearchQuery] = useState(
    savedListState?.detailedSearchQuery || ''
  );
  const isDetailedSearchActive = React.useMemo(() => {
    return (detailedSearchQuery || '').toString().trim().length > 0;
  }, [detailedSearchQuery]);
  const [dsName, setDsName] = useState(savedDetailedForm?.dsName ?? '');
  const [dsEmail, setDsEmail] = useState(savedDetailedForm?.dsEmail ?? '');
  const [dsPhone, setDsPhone] = useState(savedDetailedForm?.dsPhone ?? '');

  const [lazyParams, setLazyParams] = useState(() => {
    const savedLazyParams = savedListState?.lazyParams;
    if (savedLazyParams) {
      return {
        ...savedLazyParams,
        first: savedLazyParams.first ?? 0,
        rows: savedLazyParams.rows ?? rows,
        page: savedLazyParams.page ?? 0,
        sortField: savedLazyParams.sortField || DEFAULT_SORT_FIELD,
        sortOrder: typeof savedLazyParams.sortOrder === 'number'
          ? savedLazyParams.sortOrder
          : DEFAULT_SORT_ORDER,
      };
    }
    return {
      first: 0,
      rows: rows,
      page: 0,
      sortField: DEFAULT_SORT_FIELD,
      sortOrder: DEFAULT_SORT_ORDER,
    };
  });

  let loadLazyTimeout = null;

  const apiCall = (method, url, data) => {
    return axios({
      method: method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      params: {},
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
        'App-Network': localStorage.getItem("networkId")
      }
    })
  }

  const loadLazyData = () => {
    setLoading(true);

    if (loadLazyTimeout) {
      clearTimeout(loadLazyTimeout);
    }

    loadLazyTimeout = setTimeout(() => {
      let query = window.App.transformQueryParams(lazyParams);
      let extraQuery = '';
      let finalDetailedQuery = detailedSearchQuery;
      if (props.forcedSearchQuery) {
        finalDetailedQuery = finalDetailedQuery 
          ? finalDetailedQuery + (finalDetailedQuery.endsWith('&') || props.forcedSearchQuery.startsWith('&') ? '' : '&') + props.forcedSearchQuery
          : props.forcedSearchQuery;
      }
      if (finalDetailedQuery) {
        if (!query || query.toString().length === 0) {
          query = '?' + finalDetailedQuery;
        } else if (query.toString().includes('?')) {
          query = query + (query.toString().endsWith('?') || query.toString().endsWith('&') ? '' : '&') + finalDetailedQuery;
        } else {
          query = '?' + query + '&' + finalDetailedQuery;
        }
      }

      apiCall('get', '/api/v2/contact' + query)
        .then(response => {
          const items = response.data.data.items || [];

          setContacts(items);
          setTotalRecords(response.data.data.total_item_count || items.length);
        })
        .catch(error => {
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        })
        .finally(() => {
          setLoading(false);
        })
    }, Math.random() * 1000 + 250);
  }

  const onPage = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    setLazyParams(_lazyParams);
  }

  const onSort = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    setLazyParams(_lazyParams);
  }

  const onFilter = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    _lazyParams['first'] = 0;
    _lazyParams['page'] = 0;
    setLazyParams(_lazyParams);
  }

  const pager = {
    layout: 'RowsPerPageDropdown CurrentPageReport PrevPageLink NextPageLink',
    'RowsPerPageDropdown': (options) => {
      const dropdownOptions = [
        {label: Number(process.env.REACT_APP_LIST_ROW_OP1), value: Number(process.env.REACT_APP_LIST_ROW_OP1)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP2), value: Number(process.env.REACT_APP_LIST_ROW_OP2)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP3), value: Number(process.env.REACT_APP_LIST_ROW_OP3)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP4), value: Number(process.env.REACT_APP_LIST_ROW_OP4)},
      ];

      return (
        <>
          <Dropdown
            value={options.value}
            options={dropdownOptions}
            onChange={(e) => {
              setRows(e.target.value);
              setLazyParams({
                ...lazyParams,
                rows: e.target.value,
                first: 0,
                page: 0
              });
            }}
            appendTo={document.body}
          />
        </>
      );
    },
    'CurrentPageReport': (options) => {
      return (
        <span style={{color: 'var(--text-color)', userSelect: 'none', width: '120px', textAlign: 'center'}}>
          {options.first} - {options.last} / {options.totalRecords}
        </span>
      )
    }
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        {localStorage.getItem('roles')?.toString().includes('role_') &&
          <Button
            icon="pi pi-pencil"
            className="p-button-warning p-mr-2"
            tooltip={t('edit')}
            tooltipOptions={{position: 'left'}}
            onClick={() => {
              history.push('/partner/contact?update=contact&contactId=' + rowData.id);
            }}
          />
        }

        <Button
          icon="pi pi-eye"
          className="p-button p-mr-2"
          tooltip={t('view')}
          tooltipOptions={{position: 'left'}}
          onClick={() => {
            history.push('/partner/contact?read=contact&contactId=' + rowData.id);
          }}
        />

        {localStorage.getItem('roles')?.toString().includes('role_') &&
          <Button
            icon="pi pi-trash"
            className="p-button-danger"
            tooltip={t('delete')}
            tooltipOptions={{position: 'left'}}
            onClick={() => {
              if (window.confirm('A törlés megerősítésére van szükség!')) {
                setLoading(true);

                apiCall('delete', '/api/v2/contact/' + rowData.id)
                  .then(response => {
                    window.App.toastShow('success', 'Sikeres törlés!');
                    window.App.setState({rerenderContactList: Math.random()});
                  })
                  .catch(error => {
                    window.App.toastShow('error', t('error'), error.toString(), '', error.response);
                  })
                  .finally(() => {
                    setLoading(false);
                  })
              }
            }}
          />
        }
      </React.Fragment>
    );
  }

  const persistDetailedForm = () => {
    const payload = {
      dsEmail,
      dsPhone,
      dsName,
    };
    localStorage.setItem(DS_STORAGE_KEY, JSON.stringify(payload));
  };

  const clearDetailedFormPersist = () => {
    localStorage.removeItem(DS_STORAGE_KEY);
  };

  const buildDetailedSearchQuery = () => {
    let parts = [];
    if (dsName) {
      parts.push(`search[name][operator]=like&search[name][value]=*${encodeURIComponent(dsName)}*`);
    }
    if (dsEmail) {
      parts.push(`search[email][operator]=like&search[email][value]=*${encodeURIComponent(dsEmail)}*`);
    }
    if (dsPhone) {
      parts.push(`search[phone][operator]=like&search[phone][value]=*${encodeURIComponent(dsPhone)}*`);
    }
    return parts.join('&');
  }

  const doDetailedSearch = () => {
    const q = buildDetailedSearchQuery();
    setDetailedSearchQuery(q);
    setLazyParams((prev) => ({
      ...prev,
      first: 0,
      page: 0,
    }));
  };

  useEffect(() => {
    loadLazyData();
  }, [lazyParams, detailedSearchQuery, window.App.state.rerenderContactList])

  useEffect(() => {
    localStorage.setItem(LIST_STORAGE_KEY, JSON.stringify({
      rows,
      lazyParams,
      detailedSearchQuery,
      sortField: lazyParams.sortField || DEFAULT_SORT_FIELD,
      sortOrder: typeof lazyParams.sortOrder === 'number' ? lazyParams.sortOrder : DEFAULT_SORT_ORDER,
    }));
  }, [rows, lazyParams, detailedSearchQuery]);

  useEffect(() => {
    if (typeof onDetailedSearchActiveChange === 'function') {
      onDetailedSearchActiveChange(isDetailedSearchActive);
    }
  }, [isDetailedSearchActive, onDetailedSearchActiveChange]);

  return (
    <>
      {(detailedSearchVisible  || view === 'search') &&
              <Panel className="p-mb-2" onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    doDetailedSearch();
                  }
                }}>
                  <div className={"p-grid"}>
                    <div className={"p-col-12 p-lg-3"}>
                      <div className="p-field p-fluid">
                        <label>{t('columnName2')}</label>
                        <InputText value={dsName} onChange={(e) => setDsName(e.target.value)} />
                      </div>
                    </div>
                    <div className={"p-col-12 p-lg-3"}>
                      <div className="p-field p-fluid">
                        <label>{t('columnEmail')}</label>
                        <InputText value={dsEmail} onChange={(e) => setDsEmail(e.target.value)} />
                      </div>
                    </div>
                    <div className={"p-col-12 p-lg-3"}>
                      <div className="p-field p-fluid">
                        <label>{t('columnPhoneNumber')}</label>
                        <InputText value={dsPhone} onChange={(e) => setDsPhone(e.target.value)} />
                      </div>
                    </div>
                  </div>
                  <div className={"p-col-12 p-text-right"}>
                    <Button
                      type="button"
                      label={t('dsClearCriteria')}
                      icon="pi pi-times"
                      className="p-button-danger p-mr-2"
                      onClick={() => {
                        setDsName('');
                        setDsEmail('');
                        setDsPhone('');
                        setDetailedSearchQuery('');
                        clearDetailedFormPersist();
                        localStorage.removeItem(LIST_STORAGE_KEY);
                        setLazyParams({
                          first: 0,
                          rows: rows,
                          page: 0,
                          sortField: 'name',
                          sortOrder: 1,
                        });
                      }}
                    />
                    <Button
                      type="button"
                      label={t('dsSearch')}
                      icon="pi pi-search"
                      className="p-button-primary"
                      onClick={() => {
                        const q = buildDetailedSearchQuery();
                        setDetailedSearchQuery(q);
                        persistDetailedForm();
                        setLazyParams({
                          ...lazyParams,
                          first: 0,
                          page: 0,
                        });
                      }}
                    />
                  </div>
                </Panel>
      }
      <DataTable
        size={localStorage.getItem('scale')}
        emptyMessage={t('noResults')}
        value={contacts}
        paginator
        first={lazyParams.first}
        totalRecords={totalRecords}
        onPage={onPage}
        onSort={onSort}
        sortField={lazyParams.sortField}
        sortOrder={lazyParams.sortOrder}
        onFilter={onFilter}
        filters={lazyParams.filters}
        loading={loading}
        lazy
        paginatorTemplate={pager}
        rows={rows}
        autoLayout={true}
        responsiveLayout="scroll"
        stripedRows
        ref={dt}
        dataKey="id"
      >
        <Column field="name" header={t('columnName')} sortable/>
        <Column field="email" header={t('columnEmail')} sortable/>
        <Column field="phone" header={t('columnPhoneNumber')} sortable/>
        <Column field="note" header={t('columnComment')} sortable/>
        <Column body={actionBodyTemplate} className={"p-py-0 p-text-right p-text-nowrap"} />
      </DataTable>
    </>
  )
}

export default withTranslation('common')(ContactListComponent);